import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

const EMPTY_VALUE = "__DOXA_SELECT_EMPTY__";

export default function DoxaSelect({
  value,
  onValueChange,
  options = [],
  placeholder = "Selecione",
  disabled = false,
  className,
  contentClassName,
  align = "start",
  sideOffset = 6,
  testId,
  ariaLabel,
  size = "default",
}) {
  const internalValue = value === "" || value === null || value === undefined ? EMPTY_VALUE : String(value);
  const normalizedOptions = options.map((option) => ({
    ...option,
    radixValue: option.value === "" || option.value === null || option.value === undefined
      ? EMPTY_VALUE
      : String(option.value),
  }));
  const selectedOption = normalizedOptions.find((option) => option.radixValue === internalValue);
  const selectedTitle = typeof selectedOption?.label === "string" || typeof selectedOption?.label === "number"
    ? String(selectedOption.label)
    : undefined;

  const handleValueChange = (nextValue) => {
    onValueChange?.(nextValue === EMPTY_VALUE ? "" : nextValue);
  };

  return (
    <SelectPrimitive.Root value={internalValue} onValueChange={handleValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        data-testid={testId}
        title={selectedTitle}
        className={cn(
          "doxa-select-trigger group relative flex min-w-0 max-w-full items-center overflow-hidden rounded-xl border border-[#E0D9CD] bg-white text-sm text-[#1F1F1F] outline-none transition sm:min-w-[150px]",
          size === "compact" ? "h-11 pl-3 pr-10 sm:h-9 sm:pl-2.5 sm:pr-9" : "h-11 pl-3 pr-10 sm:h-10 sm:pr-9",
          "hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15",
          "disabled:cursor-not-allowed disabled:bg-[#F6F3EE] disabled:text-[#A59C90] disabled:opacity-70",
          "data-[state=open]:border-[#F75A1B] data-[state=open]:ring-2 data-[state=open]:ring-[#F75A1B]/10",
          className
        )}
      >
        <span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left leading-none [&>span]:block [&>span]:max-w-full [&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:whitespace-nowrap">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 shrink-0 -translate-y-1/2 text-[#8A8378] transition-transform duration-150 group-data-[state=open]:rotate-180 sm:right-2.5" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          align={align}
          sideOffset={sideOffset}
          className={cn(
            "z-50 max-h-[320px] min-w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-xl border border-[#E0D9CD] bg-white p-1.5 text-[#1F1F1F]",
            "shadow-[0_14px_40px_rgba(45,31,20,.14)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
            contentClassName
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1 text-[#8A8378]">
            <ChevronUp className="h-4 w-4" />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="max-h-[280px] max-w-full">
            {normalizedOptions.map((option) => (
              <SelectPrimitive.Item
                key={option.radixValue}
                value={option.radixValue}
                disabled={option.disabled}
                className={cn(
                  "relative flex min-w-0 max-w-full cursor-pointer select-none items-center overflow-hidden rounded-lg py-2.5 pl-3 pr-9 text-sm outline-none transition-colors",
                  "focus:bg-[#FFF0E9] focus:text-[#D94A12] data-[state=checked]:bg-[#FFF7F2] data-[state=checked]:font-semibold data-[state=checked]:text-[#D94A12]",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-45"
                )}
              >
                <SelectPrimitive.ItemText>
                  <span className="block min-w-0 truncate">{option.label}</span>
                </SelectPrimitive.ItemText>
                <span className="absolute right-2.5 grid h-5 w-5 place-items-center text-[#F75A1B]">
                  <SelectPrimitive.ItemIndicator>
                    <Check className="h-4 w-4" />
                  </SelectPrimitive.ItemIndicator>
                </span>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1 text-[#8A8378]">
            <ChevronDown className="h-4 w-4" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
